'use client';

import { c as _c } from "react/compiler-runtime";
import { updateCouponOutcome } from '@svenskaspelab/poolbetting/core/action/coupon';
import { useDraw } from '@svenskaspelab/poolbetting/core/frameworks/nextjs/draw';
import { getOutcomeCount } from '@svenskaspelab/poolbetting/core/lib/client';
import { useCouponId, useCouponOutcomes, useCouponSettings, useCouponType } from '@svenskaspelab/poolbetting/core/nextjs/coupon';
import { useEngine } from '@svenskaspelab/poolbetting/core/nextjs/engine';
import type { CouponIndex, Outcome } from '@svenskaspelab/poolbetting/schema/coupon';
import type { DrawData, DrawNumber } from '@svenskaspelab/poolbetting/schema/draw';
import type { EventNumber, EventType, SportType } from '@svenskaspelab/poolbetting/schema/sport';
import { CouponEventOutcomes, CouponEventOutcomesSrow, CouponOutcome, CouponOutcomeSrow } from '@svenskaspelab/poolbetting/ui/Coupon';
import { breakpoints, useMediaQueryAbove, useMediaQueryBelow, useMediaQueryBetween } from '@svenskaspelab/ui/react/media-query';
import { type ReactNode, useCallback, useEffect, useRef } from 'react';
import { getButtonSize } from './utils/getButtonSize';
type CouponOutcomesContainerProps = {
  eventNumber: EventNumber;
  sportType: SportType;
  eventType: EventType;
  couponIndex: CouponIndex;
  className?: string;
  drawNumber: DrawNumber;
};
type TapIntent = {
  index: Outcome;
  couponIndex?: CouponIndex;
};
const getOutcomeDistribution = (drawData: DrawData | undefined, eventNumber: EventNumber): (string | null | undefined)[] | undefined => {
  if (!drawData) {
    return undefined;
  }
  const drawEvent = drawData.draw.events.find(event => event.eventNumber === eventNumber);
  if (!drawEvent || drawEvent.status !== 'COMPLETE') {
    return undefined;
  }
  const eventTypeStatisticsId = drawData.bettingEvent[drawEvent.bettingEventId]?.eventTypeStatisticsId;
  if (!eventTypeStatisticsId) {
    return undefined;
  }
  const eventStatistics = drawData.eventTypeStatistics[eventTypeStatisticsId];
  if (!eventStatistics) {
    return undefined;
  }
  return eventStatistics.distribution.current.value;
};
const getUpdatedOutcomes = (currentOutcomes: Outcome[], outcomeIndex: Outcome): Outcome[] => {
  const nextOutcomes = currentOutcomes.slice();
  const index = nextOutcomes.indexOf(outcomeIndex);
  if (index === -1) {
    nextOutcomes.push(outcomeIndex);
  } else {
    nextOutcomes.splice(index, 1);
  }
  return nextOutcomes;
};

/**
 * Component responsible for rendering the outcomes for a specific event. It uses product, eventType and sportType to determine the amount of outcomes.
 * Also this component is responsible for toggling the outcome and and fetching the selected outcomes.
 *
 * @param root0 props
 * @param root0.eventNumber Number for event
 * @param root0.sportType Sport for event
 * @param root0.eventType Type of event (event_1x2, goal_count etc)
 * @param root0.couponIndex Index of the coupon, used for multi coupon
 * @param root0.className Optional classname for the component
 * @param root0.drawNumber Optional draw number for the event
 * @returns component that renders outcomes for a specific event
 */
export default function CouponOutcomesContainer(t0) {
  const $ = _c(49);
  const {
    eventNumber,
    sportType,
    eventType,
    couponIndex,
    className,
    drawNumber
  } = t0;
  const [couponId] = useCouponId();
  let t1;
  if ($[0] !== couponId || $[1] !== couponIndex) {
    t1 = {
      couponId,
      couponIndex
    };
    $[0] = couponId;
    $[1] = couponIndex;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const couponType = useCouponType(t1);
  const isSingleRows = couponType === "singlerows";
  const definition = useEngine();
  let t2;
  if ($[3] !== definition.model || $[4] !== eventType || $[5] !== sportType) {
    t2 = getOutcomeCount(sportType, eventType, definition.model);
    $[3] = definition.model;
    $[4] = eventType;
    $[5] = sportType;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const outcomeCount = t2;
  const settings = useCouponSettings(definition.moduleEngine, "PLAY");
  let t3;
  if ($[7] !== definition.moduleEngine || $[8] !== drawNumber) {
    t3 = {
      drawNumber,
      product: definition.moduleEngine
    };
    $[7] = definition.moduleEngine;
    $[8] = drawNumber;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const draw = useDraw(t3);
  let t4;
  if ($[10] !== draw.data || $[11] !== eventNumber || $[12] !== settings.data.outcomeLabel) {
    t4 = settings.data.outcomeLabel === "LocalDistribution" ? getOutcomeDistribution(draw.data, eventNumber) : undefined;
    $[10] = draw.data;
    $[11] = eventNumber;
    $[12] = settings.data.outcomeLabel;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const localDistribution = t4;
  const {
    regBetDisabled
  } = draw.data.draw;
  let t5;
  if ($[14] !== couponId || $[15] !== couponIndex || $[16] !== eventNumber) {
    t5 = {
      couponId,
      couponIndex,
      eventNumber,
      groupIndex: 0
    };
    $[14] = couponId;
    $[15] = couponIndex;
    $[16] = eventNumber;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const outcomes = useCouponOutcomes(t5);
  const isMobile = useMediaQueryBelow(breakpoints.sm);
  const isTablet = useMediaQueryBetween(breakpoints.sm, breakpoints.md);
  const isDesktop = useMediaQueryAbove(breakpoints.md);
  let t6;
  if ($[18] !== isDesktop || $[19] !== isMobile || $[20] !== isTablet) {
    t6 = getButtonSize({
      isDesktop,
      isTablet,
      isMobile
    });
    $[18] = isDesktop;
    $[19] = isMobile;
    $[20] = isTablet;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const buttonSize = t6;
  const outcomesRef = useRef(outcomes);
  const tapIntentRef = useRef(undefined);
  let t7;
  let t8;
  if ($[22] !== outcomes) {
    t7 = () => {
      outcomesRef.current = outcomes;
    };
    t8 = [outcomes];
    $[22] = outcomes;
    $[23] = t7;
    $[24] = t8;
  } else {
    t7 = $[23];
    t8 = $[24];
  }
  useEffect(t7, t8);
  let t9;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      const tapIntent = tapIntentRef.current;
      tapIntentRef.current = undefined;
      return tapIntent;
    };
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const consumeTapIntent = t9;
  let t10;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      tapIntentRef.current = undefined;
    };
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const clearTapIntent = t10;
  let t11;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = (index, tapIntentCouponIndex) => {
      tapIntentRef.current = {
        index,
        couponIndex: tapIntentCouponIndex
      };
    };
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const setTapIntent = t11;
  let t12;
  if ($[28] !== couponId || $[29] !== couponIndex || $[30] !== eventNumber || $[31] !== isSingleRows) {
    t12 = (outcomeIndex, callbackCouponIndex) => {
      const tapIntentAtRequest = consumeTapIntent();
      const shouldUseTapIntent = tapIntentAtRequest !== undefined && tapIntentAtRequest.index !== outcomeIndex;
      const resolvedOutcomeIndex = shouldUseTapIntent ? tapIntentAtRequest.index : outcomeIndex;
      const currentOutcomes = outcomesRef.current;
      const updatedOutcomes = isSingleRows ? [resolvedOutcomeIndex] : getUpdatedOutcomes(currentOutcomes, resolvedOutcomeIndex);
      const oldOutcomes = outcomesRef.current;
      outcomesRef.current = updatedOutcomes;
      const expectedOutcoms = outcomesRef.current;
      updateCouponOutcome(updatedOutcomes, {
        couponId,
        couponIndex: callbackCouponIndex ?? couponIndex,
        boardIndex: 0,
        eventNumber,
        groupIndex: 0
      }).then(result => {
        if (result === "ERROR" && outcomesRef.current === expectedOutcoms) {
          outcomesRef.current = oldOutcomes;
        }
      }).catch(() => {
        if (outcomesRef.current === expectedOutcoms) {
          outcomesRef.current = oldOutcomes;
        }
      });
    };
    $[28] = couponId;
    $[29] = couponIndex;
    $[30] = eventNumber;
    $[31] = isSingleRows;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const toggleOutcome = t12;
  const CouponEventOutcomesComponent = isSingleRows ? CouponEventOutcomesSrow : CouponEventOutcomes;
  const CouponOutcomeComponent = isSingleRows ? CouponOutcomeSrow : CouponOutcome;
  let t13;
  if ($[33] !== CouponOutcomeComponent || $[34] !== buttonSize || $[35] !== couponIndex || $[36] !== eventNumber || $[37] !== regBetDisabled) {
    t13 = props => <CouponOutcomeComponent {...props} eventNumber={eventNumber} couponIndex={couponIndex} buttonSize={buttonSize} isDisabled={regBetDisabled === "Regbet" ? true : undefined} key={props.index} onPointerDown={() => {
      setTapIntent(props.index as Outcome, couponIndex);
    }} onTouchStart={() => {
      setTapIntent(props.index as Outcome, couponIndex);
    }} onPointerCancel={clearTapIntent} onTouchCancel={clearTapIntent} />;
    $[33] = CouponOutcomeComponent;
    $[34] = buttonSize;
    $[35] = couponIndex;
    $[36] = eventNumber;
    $[37] = regBetDisabled;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== CouponEventOutcomesComponent || $[40] !== className || $[41] !== couponIndex || $[42] !== eventNumber || $[43] !== localDistribution || $[44] !== outcomeCount || $[45] !== outcomes || $[46] !== t13 || $[47] !== toggleOutcome) {
    t14 = <CouponEventOutcomesComponent className={className} selectedOutcomes={outcomes} outcomeCount={outcomeCount} onChange={toggleOutcome} eventNumber={eventNumber} couponIndex={couponIndex} localDistribution={localDistribution} renderItem={t13} />;
    $[39] = CouponEventOutcomesComponent;
    $[40] = className;
    $[41] = couponIndex;
    $[42] = eventNumber;
    $[43] = localDistribution;
    $[44] = outcomeCount;
    $[45] = outcomes;
    $[46] = t13;
    $[47] = toggleOutcome;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  return t14;
}